import type { CloneJob, CloneJobStatus } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { isCloneJobActive, useCloneJobs } from "@/lib/hooks/use-clone-jobs";
import { invalidateFlows } from "@/lib/hooks/use-flows";
import { invalidatePdfTemplates } from "@/lib/hooks/use-pdf-templates";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./clone-job-notifier.i18n";

const copyPath = (job: CloneJob) =>
  job.kind === "flow"
    ? `/settings/flows/${job.resultId}`
    : `/settings/pdf-templates/${job.resultId}`;

/**
 * QL-334 — mounted once in the app shell. When a background copy finishes it
 * refreshes the flow and PDF template lists, and tells the user who started
 * it, on whatever page they are, with a link to the copy.
 */
export function CloneJobNotifier({ enabled }: { enabled: boolean }) {
  const t = useT(T);
  const qc = useQueryClient();
  const { jobs } = useCloneJobs(enabled);
  const lastStatus = useRef(new Map<string, CloneJobStatus>());

  useEffect(() => {
    const previous = lastStatus.current;
    lastStatus.current = new Map(jobs.map((job) => [job.id, job.status]));
    const wasActive = (id: string) => {
      const before = previous.get(id);
      return before === "pending" || before === "processing";
    };

    // Only jobs seen running and now finished: a reload does not repeat a toast.
    const finished = jobs.filter((job) => wasActive(job.id) && !isCloneJobActive(job));
    // A job seen running that left the list finished while this tab could not
    // look, or was cancelled: no toast, but its copy may be in the lists now.
    const current = new Set(jobs.map((job) => job.id));
    const vanished = [...previous.keys()].some((id) => wasActive(id) && !current.has(id));
    if (vanished || finished.some((job) => job.status === "done")) {
      void invalidateFlows(qc);
      void invalidatePdfTemplates(qc);
    }

    for (const job of finished.filter((j) => j.mine)) {
      if (job.status === "done") {
        // A custom action does not close the toast by itself, so the link does.
        const id = `clone-job-${job.id}`;
        toast.success(t.ready(job.name), {
          id,
          description:
            job.untranslatedTexts > 0 ? t.untranslatedKept(job.untranslatedTexts) : undefined,
          // Long enough to reach the link from wherever the user went meanwhile.
          duration: 30_000,
          closeButton: true,
          action: (
            <Button asChild size="sm" className="ml-auto">
              <Link to={copyPath(job)} onClick={() => toast.dismiss(id)}>
                {t.open}
              </Link>
            </Button>
          ),
        });
      } else {
        toast.error(t.failed(job.name), {
          description: job.error ?? undefined,
          closeButton: true,
        });
      }
    }
  }, [jobs, qc, t]);

  return null;
}
